import React, { useCallback, useMemo } from 'react';
import cn from 'classnames';
import SortableList, { type SortableItemState } from 'admin/components/common/SortableList';
import Divider from '../PropField/Divider';
import FillPreview from '../PropField/FillPreview';
import { Label } from '../PropField/Label';
import type { FieldConfig } from '../types';
import { layerRowsCss as rows, RemoveButton, RowGrip, RowLabel, RowValue, type InheritanceType } from '../LayerRows';
import Controls from './Controls';
import {
	fromShadowEditorData,
	stringifyShadowLayers,
	toShadowEditorData,
	type ShadowEditorData,
	type ShadowLayer,
	type ShadowLengthScale,
	type ShadowProperty,
} from './shadow-layers';
import css from './BoxShadowSettings.scss';

/*
 * The shadow editor's parts with plain props, shared by the Inspector and the Brand Kit preset editor. Hosts own the
 * value, its units and the popovers.
 */

/**
 * Shadows as sortable layer rows, first painted on top: a header with its name and add action, then the rows. Ids
 * are a layer's place plus its value, so a saved move arrives as new ids.
 */
export function ShadowLayerList(props: {
	label: React.ReactNode;
	/** The header's add action, usually a popover with the add menu */
	add: React.ReactNode;
	ids: string[];
	onMove: (from: number, to: number) => void;
	renderRow: (index: number, item: SortableItemState) => React.ReactNode;
	className?: string;
	/** Between the header and the rows, e.g. the Inspector's hidden Reset target */
	children?: React.ReactNode;
}) {
	const { ids, onMove, renderRow } = props;
	const canDrag = ids.length > 1;
	return (
		<div className={cn(rows.block, props.className)}>
			<div className={rows.header}>
				<Label component="span">{props.label}</Label>
				{props.add}
			</div>
			{props.children}

			{ids.length ? (
				<div className={canDrag ? rows.well : undefined}>
					<SortableList ids={ids} onMove={onMove}>
						{ids.map((id, index) => (
							<SortableList.Item
								// Keyed by place in the stack: keying by value would close the editor on every edit
								// eslint-disable-next-line react/no-array-index-key
								key={index}
								id={id}
								disabled={!canDrag}
							>
								{item => renderRow(index, item)}
							</SortableList.Item>
						))}
					</SortableList>
				</div>
			) : null}
		</div>
	);
}

/** One shadow row: its kind, then Remove, the value and the drag handle at the row end */
export function ShadowLayerRow(props: {
	item: SortableItemState;
	label: string;
	field?: FieldConfig;
	inheritance: InheritanceType;
	canDrag: boolean;
	onRemove: () => void;
	children: React.ReactNode;
}) {
	const { item } = props;
	return (
		<div ref={item.ref} style={item.style} className={rows.row} data-dragging={item.isDragging}>
			<RowLabel field={props.field}>{props.label}</RowLabel>
			<RowValue inheritance={props.inheritance}>
				<RemoveButton label="Remove shadow" onClick={props.onRemove} />
				{props.children}
				{props.canDrag ? <RowGrip label="Reorder shadow" handle={item.handle} /> : null}
			</RowValue>
		</div>
	);
}

/** A shadow row's value: x y blur (spread) as whole px numbers, then its color */
export function ShadowLayerValue(props: {
	layer: ShadowLayer;
	property: ShadowProperty;
	scale: ShadowLengthScale;
	/** The color as painted; undefined for a token the row cannot resolve */
	fill: string | undefined;
	token?: boolean;
	missing?: boolean;
}) {
	const { layer, property, scale } = props;
	const lengths = [layer.x, layer.y, layer.blur, ...(property === 'boxShadow' ? [layer.spread] : [])];
	return (
		<>
			{props.missing ? (
				<span className={rows.chip} data-error>
					missing
				</span>
			) : null}
			<span className={css.lengths}>{lengths.map(length => Math.round(scale.toPx(length))).join(' ')}</span>
			<FillPreview value={props.fill ?? 'none'} className={cn(props.token && css.tokenPreview)} />
		</>
	);
}

/** A shadow row's color field: the label, then the host's color control at the row end */
export function ShadowColorRow({ id, children }: { id?: string; children: React.ReactNode }) {
	return (
		<div className={rows.row}>
			<Label htmlFor={id}>Color</Label>
			<span className={rows.end}>{children}</span>
		</div>
	);
}

/**
 * One shadow's controls: type, offset, blur and spread in px, then the host's color field. `onPreview` follows a
 * drag or typing; `onChange` comes when it is let go.
 */
export function ShadowLayerFields(props: {
	layer: ShadowLayer;
	property: ShadowProperty;
	scale: ShadowLengthScale;
	/** Restarts the controls from the layer when it changes, e.g. the edited element */
	resetKey?: string;
	onPreview?: (layer: ShadowLayer) => void;
	onChange: (layer: ShadowLayer) => void;
	color: React.ReactNode;
}) {
	const { layer, property, scale, resetKey, onPreview, onChange } = props;
	const layerValue = stringifyShadowLayers([layer], property);
	// Stable while the layer is unchanged, so a redraw mid-drag doesn't reset the controls
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const initialData = useMemo(() => toShadowEditorData(layer, property, scale), [layerValue, property, resetKey]);
	const onLiveChange = useCallback(
		(data: ShadowEditorData) => onPreview?.(fromShadowEditorData(data, layer.color, scale)),
		[onPreview, layer.color, scale]
	);
	const onChangeComplete = useCallback(
		(data: ShadowEditorData) => onChange(fromShadowEditorData(data, layer.color, scale)),
		[onChange, layer.color, scale]
	);

	return (
		<div className={css.editor}>
			<Controls
				className={css.editorControls}
				initialData={initialData}
				blurMax={property === 'textShadow' ? 20 : undefined}
				onChange={onLiveChange}
				onChangeComplete={onChangeComplete}
			/>
			<Divider className={css.editorDivider} />
			{props.color}
		</div>
	);
}
